import {
  createContext, ReactElement, ReactNode, useCallback, useContext, useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '~/contexts/newToastContext';
import { HealthVariant } from '../Health/components/UnitsInfo/types';
import { parseHealthData, parseUnits } from '../helpers/health';
import { getOverviewUnitHealth } from '../helpers/overview';
import { FilterContextType } from './FilterContext';

export interface HealthCountInfo {
  healthy: number,
  alert: number,
  critical: number,
  inoperative: number,
}

export type HealthUnitsInfo = {
  id: number;
  name: string;
  lat: number | null;
  lon: number | null;
  state: string;
  city: string;
  cityId: string;
  health: HealthVariant;
  machines: HealthCountInfo;
}[]

export interface HealthDataContextType {
  data: HealthCountInfo | undefined;
  units: HealthUnitsInfo;
  loading: boolean;
  refetch: (clients: number[], selectedOptions: FilterContextType['selectedOptions']) => Promise<void>;
}

const HealthDataContext = createContext<HealthDataContextType | undefined>(undefined);

export const HealthDataProvider = ({
  children,
}: Readonly<{ children: ReactNode }>): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<HealthCountInfo | undefined>();
  const [units, setUnits] = useState<HealthUnitsInfo>([]);
  const { toast } = useToast();
  const { t } = useTranslation();

  const getHealthInfo = useCallback(async (
    clients: number[],
    selectedOptions: FilterContextType['selectedOptions'],
  ): Promise<void> => {
    setLoading(true);

    const data = await getOverviewUnitHealth(clients, {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.units?.map((item) => Number(item.value)) ?? selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
      thermalComfortStatus: selectedOptions.status,
      cityIds: selectedOptions.cities?.map((item) => item.value),
    });

    if (data) {
      setData(parseHealthData(data));
      setUnits(parseUnits(data.UNITS));
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosSaudeUnidades'), 'error');
    }

    setLoading(false);
  }, []);

  const value = useMemo((): HealthDataContextType => ({
    data,
    loading,
    units,
    refetch: getHealthInfo,
  }), [data, getHealthInfo, loading, units]);

  return (
    <HealthDataContext.Provider value={value}>
      {children}
    </HealthDataContext.Provider>
  );
};

export const useHealthDataContext = (): HealthDataContextType => {
  const context = useContext(HealthDataContext);

  if (!context) {
    throw new Error('useHealthDataContext must be used within a HealthDataProvider.');
  }

  return context;
};
